/**
 * Dispatcher Control Types
 * AIDLC Administrative Backend
 *
 * Mirrors the shapes returned by the backend dispatcher-control service
 * (server/src/services/dispatcher-control.ts).
 */

export interface DispatcherActiveAgent {
  agentId: string;
  name: string;
  subject: string | null;
  source: string | null;
  taskId: string | null;
  startedAt: string | null;
  /** True when the session outlived a kill: it is still counted as running. */
  stuck: boolean;
}

/**
 * The last time the dispatcher ended sessions on purpose. `verified` is true only
 * when every process was seen to be gone; otherwise `survivors` were still running.
 */
export interface DispatcherTermination {
  at: string;
  scope: 'kill' | 'shutdown';
  agentId: string | null;
  outcome: 'terminated' | 'survivors' | 'no-session' | 'session-changed' | 'start-cancelled';
  verified: boolean;
  /** False when child processes could not be listed, so `verified` only speaks for the sessions themselves. */
  descendantsChecked: boolean;
  survivors: number[];
  sessions: number;
}

/** Who runs the dispatcher: this server, another machine, or nobody from here. */
export type DispatcherManagedMode = 'local' | 'remote' | 'disabled';

export interface DispatcherLimits {
  maxConcurrentAgents: number;
  maxSessionsPerHour: number;
}

/** The last piece of work a limit turned away. */
export interface DispatcherRefusal {
  at: string;
  agentId: string;
  limit: string;
  reason: string;
}

export interface DispatcherStatus {
  managed: DispatcherManagedMode;
  /** Set when `managed` is not 'local': why it cannot be controlled from here. */
  managedReason: string | null;
  running: boolean;
  owned: boolean;
  pid: number | null;
  startedAt: string | null;
  heartbeat: string | null;
  heartbeatAgeMs: number | null;
  uptimeMs: number | null;
  model: string | null;
  dryRun: boolean;
  activeAgents: DispatcherActiveAgent[];
  /** The limits the running dispatcher reports it is enforcing. */
  limits: DispatcherLimits | null;
  sessionsLastHour: number | null;
  refusedCount: number;
  lastRefusal: DispatcherRefusal | null;
  /** Kept after the dispatcher has stopped. */
  lastTermination: DispatcherTermination | null;
}

export interface DispatcherConfig extends DispatcherLimits {
  model: string | null;
  defaultWorkingPhrase: string;
  workingPhrases: Record<string, string>;
}

export interface DispatcherLogEntry {
  /** Counts up for the life of the server process. With `ts` it identifies a line, and is the list key. */
  seq: number;
  ts: string;
  type: 'stdout' | 'stderr' | 'system';
  line: string;
}

export interface DispatcherActionResult {
  ok: boolean;
  reason?: string;
  /** True when the process had to be ended by force. */
  forced?: boolean;
  /** Agent session processes that could not be confirmed stopped. Absent when nobody checked. */
  survivors?: number[];
  /** After a stop: true only when every agent session was seen to be gone. */
  sessionsVerified?: boolean;
  /** After a stop: false when child processes could not be listed. */
  descendantsChecked?: boolean;
  status: DispatcherStatus;
}

export interface DispatcherCommandResult {
  ok: boolean;
  reason?: string;
  verified?: boolean;
  /** False when child processes could not be listed, so only the session itself was checked. */
  descendantsChecked?: boolean;
  survivors?: number[];
}

/** The bounds the server enforces; the form uses the same ones. */
export const CONCURRENT_AGENTS_BOUNDS = { min: 1, max: 12 } as const;
export const SESSIONS_PER_HOUR_BOUNDS = { min: 1, max: 200 } as const;
export const MAX_PHRASE_LENGTH = 80;
